'use client';

import { useState, type ReactNode, type CSSProperties } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { cn } from '../../lib/cn';
import { type SColor } from '../../lib/color';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { SIcon, type SIconName } from '../SIcon';
import { SGhostButton } from '../SGhostButton';
import { SButton, type SButtonColor } from '../SButton';
import { STextLink } from '../STextLink';

export type SPopoverPlacement = 'top' | 'bottom' | 'left' | 'right';
/** 색상 타입 — default: 다크 배경 / danger·warning·accent: 라이트 배경 (component.popover 토큰) */
export type SPopoverType = 'default' | 'danger' | 'warning' | 'accent';

// 하단 우측 버튼 스타일은 type 별 고정 (default/accent=primary solid, danger=danger outline, warning=neutral outline)
const BUTTON_PRESET: Record<SPopoverType, { color: SButtonColor; outline: boolean }> = {
  default: { color: 'primary', outline: false },
  accent: { color: 'primary', outline: false },
  danger: { color: 'danger', outline: true },
  warning: { color: 'neutral', outline: true },
};

export interface SPopoverProps {
  /** 표시 여부 (제어). onOpenChange 와 함께 사용 */
  open?: boolean;
  /** 기본 표시 여부 (비제어) */
  defaultOpen?: boolean;
  /** 표시 상태 변경 (sdShowChange) */
  onOpenChange?: (open: boolean) => void;
  /** 표시 방향 */
  placement?: SPopoverPlacement;
  /** 색상 타입 (default: 다크 / danger·warning·accent: 라이트 배경) */
  type?: SPopoverType;
  /** 메뉴 제목 (필수) */
  menuTitle: string;
  /** 제목 좌측 아이콘 (선택) */
  titleIcon?: SIconName;
  /** 본문 내용 */
  children?: ReactNode;
  /** 커스텀 트리거. 없으면 아이콘 트리거 렌더 */
  trigger?: ReactNode;
  /** 기본(아이콘) 트리거 설정 */
  icon?: SIconName;
  iconSize?: number;
  /** 트리거 아이콘 색상. 팔레트 키(`grey_65`, `red_95` …) 또는 임의 CSS 색상 */
  color?: SColor;
  /** 하단 좌측 링크 레이블 (선택). 스타일은 chevron 링크로 고정 */
  leftLinkLabel?: string;
  /** 하단 좌측 링크 클릭 */
  onLeftLinkClick?: () => void;
  /** 하단 우측 버튼 레이블 (필수). 색상/스타일은 type 별로 고정 */
  buttonLabel: string;
  /** 하단 우측 버튼 클릭 */
  onButtonClick?: () => void;
  ariaLabel?: string;
  menuClassName?: string;
  style?: CSSProperties;
}

/**
 * SPopover — sd-popover 포팅 (Radix SPopover 기반).
 * 아이콘/커스텀 트리거, menuTitle, 본문, 하단 leftLink(STextLink)+button(SButton) 푸터.
 * 색상은 `--cmp-popover-default-*` 토큰(다크 배경).
 */
export function SPopover({
  open,
  defaultOpen,
  onOpenChange,
  placement = 'bottom',
  type = 'default',
  menuTitle,
  titleIcon,
  children,
  trigger,
  icon = 'helpOutline',
  iconSize = 12,
  color = '#01BB4B',
  leftLinkLabel,
  onLeftLinkClick,
  buttonLabel,
  onButtonClick,
  ariaLabel,
  menuClassName,
  style,
}: SPopoverProps) {
  const portalContainer = usePortalContainer();
  // controlled(open)/uncontrolled(defaultOpen) 하이브리드 — close 버튼에서 setOpen 호출용
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const isControlled = open !== undefined;
  const actualOpen = isControlled ? open : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  // 하단 우측 버튼은 필수 → 푸터 항상 렌더. 좌측 링크는 선택.
  const btnPreset = BUTTON_PRESET[type];
  const bg = `var(--cmp-popover-${type}-bg)`;
  const titleColor = `var(--cmp-popover-${type}-title)`;
  // danger·warning·accent 는 라이트 배경 → light 토큰의 description(어두운 글자) 사용
  const descColor =
    type === 'default'
      ? 'var(--cmp-popover-default-description)'
      : 'var(--cmp-popover-light-description)';
  // 좌측 링크 텍스트 색: default=subAction(#D8D8D8, 다크배경용) / 그 외=light subAction(#888888)
  const leftLinkLabelClass =
    type === 'default'
      ? 'text-[color:var(--cmp-popover-default-subAction)]'
      : 'text-[color:var(--cmp-popover-light-subAction)]';

  const triggerNode = trigger ?? (
    <span className="inline-flex cursor-pointer" aria-label={ariaLabel}>
      <SIcon name={icon} size={iconSize} color={color} />
    </span>
  );

  return (
    <RPopover.Root open={actualOpen} onOpenChange={setOpen}>
      <RPopover.Trigger asChild>{triggerNode}</RPopover.Trigger>
      <RPopover.Portal container={portalContainer}>
        <RPopover.Content
          side={placement}
          sideOffset={8}
          className={cn(
            // 닫기 버튼이 항상 있으므로 우측은 close clearance(36px) 로 고정
            'relative z-50 flex min-w-[160px] flex-col gap-[var(--cmp-popover-gap)] rounded-[var(--cmp-popover-radius)] py-[var(--cmp-popover-paddingY)] px-[var(--cmp-popover-paddingX)] text-[12px] leading-[20px] shadow-normal-sm',
            FLOATING_SLIDE_ANIM,
            menuClassName,
          )}
          style={{ background: bg, color: descColor, ...style }}
        >
          {/* 닫기 — SGhostButton (다크 배경 default=inverse 흰색 / 라이트 배경=default 회색) */}
          <SGhostButton
            icon="close"
            size="xs"
            ariaLabel="닫기"
            onClick={() => setOpen(false)}
            className="absolute right-[12px] top-[12px]"
          />
          {(menuTitle || children) && (
            <div className="flex min-w-0 flex-col gap-[var(--cmp-popover-contents-gap)]">
              {menuTitle && (
                <div
                  className="flex items-center gap-[var(--cmp-popover-title-gap)] text-[14px] font-bold leading-[24px]"
                  style={{ color: titleColor }}
                >
                  {titleIcon && <SIcon name={titleIcon} size={16} color={titleColor} />}
                  <span className="min-w-0">{menuTitle}</span>
                </div>
              )}
              {children && <div className="min-w-0">{children}</div>}
            </div>
          )}
          <div
            className={cn('flex items-center', leftLinkLabel ? 'justify-between' : 'justify-end')}
          >
            {/* 좌측 링크: 레이블만 받고 스타일은 chevron 링크로 고정 */}
            {leftLinkLabel && (
              <STextLink
                label={leftLinkLabel}
                labelClass={leftLinkLabelClass}
                onClick={onLeftLinkClick}
              />
            )}
            {/* 우측 버튼: 레이블만 받고 color/outline 은 type 별 고정 */}
            <SButton
              color={btnPreset.color}
              outline={btnPreset.outline}
              size="xs"
              onClick={onButtonClick}
              label={buttonLabel}
            />
          </div>
          <RPopover.Arrow width={16} height={12} style={{ fill: bg }} />
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}
